import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Avatar,
  Chip,
  Divider,
  Badge,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Add as AddIcon,
  SwapHoriz as SwapIcon,
  Home as HomeIcon,
  ChevronRight as ChevronRightIcon,
  Inbox as InboxIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { PageHeader, LoadingSpinner, EmptyState, DataCard } from '../../components/common';
import { CustodyFormDialog, CustodySwapRequestDialog } from '../../components/dialogs';
import { useCustodyEvents } from '../../hooks';
import { useFamily, useAuth } from '../../context';
import { CustodyPlanWithId, SwapRequest } from '../../types';
import { formatCustodyDate } from '../../utils/custody';
import { colors } from '../../theme';

const CustodyPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const { currentFamily: family } = useFamily();

  const {
    custodyPlans,
    households,
    custodyDates,
    loading,
    familyHouseholdData,
    saveCustodySchedule,
    closePlanAtDate,
  } = useCustodyEvents();

  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [swapDialogMode, setSwapDialogMode] = useState<'create' | 'view'>('create');
  const [selectedPlan, setSelectedPlan] = useState<CustodyPlanWithId | null>(null);
  const [isNewPlan, setIsNewPlan] = useState(false);

  const today = useMemo(() => new Date(), []);

  const householdType = familyHouseholdData?.householdType;
  const swapRequests: SwapRequest[] = useMemo(
    () => familyHouseholdData?.swapRequests || [],
    [familyHouseholdData?.swapRequests]
  );

  // Get active plan
  const activePlan = useMemo(() => {
    const now = today.getTime();
    return custodyPlans.find(
      (p) => p.plan.startDate <= now && p.plan.endDate >= now
    );
  }, [custodyPlans, today]);

  // Count pending swap requests
  const pendingSwapCount = useMemo(() => {
    return swapRequests.filter((r) => r.status === 'pending').length;
  }, [swapRequests]);

  // Check if a plan is active
  const isPlanActive = useCallback(
    (startDate: number, endDate: number) => {
      const now = today.getTime();
      return startDate <= now && endDate >= now;
    },
    [today]
  );

  // Check if a plan is past
  const isPlanPast = useCallback(
    (endDate: number) => {
      return endDate < today.getTime();
    },
    [today]
  );

  const handleAddPlan = () => {
    setSelectedPlan(null);
    setIsNewPlan(true);
    setFormDialogOpen(true);
  };

  const handleEditPlan = (plan: CustodyPlanWithId) => {
    setSelectedPlan(plan);
    setIsNewPlan(false);
    setFormDialogOpen(true);
  };

  const handleSavePlan = async (plan: any, planHouseholds: Record<string, any>) => {
    if (!family?.id || !user?.id) {
      enqueueSnackbar('Missing family or user information', { variant: 'error' });
      return;
    }

    try {
      // If creating a new plan and there's an active plan, close the active plan first
      if (isNewPlan && activePlan) {
        const dayBeforeStart = new Date(plan.startDate);
        dayBeforeStart.setDate(dayBeforeStart.getDate() - 1);
        dayBeforeStart.setHours(23, 59, 59, 999);
        await closePlanAtDate(activePlan.id, dayBeforeStart);
      }

      await saveCustodySchedule(
        {},
        family.id,
        user.id,
        plan,
        planHouseholds,
        selectedPlan?.id || null
      );

      enqueueSnackbar(
        selectedPlan ? 'Custody plan updated' : 'Custody plan created',
        { variant: 'success' }
      );
      setFormDialogOpen(false);
    } catch (err) {
      enqueueSnackbar('Failed to save custody plan', { variant: 'error' });
      throw err;
    }
  };

  const handleSwapRequest = () => {
    setSwapDialogMode('create');
    setSwapDialogOpen(true);
  };

  const handleViewRequests = () => {
    setSwapDialogMode('view');
    setSwapDialogOpen(true);
  };

  if (loading) {
    return <LoadingSpinner message="Loading custody plans..." />;
  }

  // Show message for non-split households
  if (householdType !== 'split') {
    return (
      <Box>
        <PageHeader
          title="Custody Plans"
          subtitle="Manage custody schedules for split households"
        />

        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <HomeIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Single Household
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
              Custody plans are only available for split households. Update your
              family settings to enable this feature.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Custody Plans"
        subtitle="Manage custody schedules and swap requests"
        actionLabel="Create Plan"
        actionIcon={AddIcon}
        onAction={handleAddPlan}
      />

      <Grid container spacing={3}>
        {/* Summary Cards */}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard
            title="Active Plan"
            icon={CalendarIcon}
            iconColor={colors.calendar}
            value={activePlan ? '1' : '0'}
            subtitle={activePlan ? activePlan.plan.custodySchemeText : 'No active plan'}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard
            title="Total Plans"
            icon={ScheduleIcon}
            iconColor="#8B5CF6"
            value={custodyPlans.length}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard
            title="Households"
            icon={HomeIcon}
            iconColor="#F59E0B"
            value={Object.keys(households).length}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DataCard
            title="Pending Swaps"
            icon={SwapIcon}
            iconColor="#EF4444"
            value={pendingSwapCount}
            onClick={handleSwapRequest}
          />
        </Grid>

        {/* Swap Actions */}
        {activePlan && (
          <Grid size={12}>
            <Card>
              <CardContent sx={{ pb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<SwapIcon />}
                      onClick={handleSwapRequest}
                      sx={{ justifyContent: 'flex-start', py: 1.5 }}
                    >
                      Request Date Swap
                    </Button>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={
                        <Badge badgeContent={pendingSwapCount} color="error">
                          <InboxIcon />
                        </Badge>
                      }
                      onClick={handleViewRequests}
                      sx={{ justifyContent: 'flex-start', py: 1.5 }}
                    >
                      View Requests
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Household Legend */}
        {Object.keys(households).length > 0 && (
          <Grid size={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Households
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {Object.values(households).map((hh) => (
                    <Box key={hh.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: 1,
                          bgcolor: hh.color,
                        }}
                      />
                      <Typography variant="body2">{hh.name}</Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Plans List */}
        <Grid size={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Your Plans
              </Typography>

              {custodyPlans.length === 0 ? (
                <EmptyState
                  icon={CalendarIcon}
                  title="No Custody Plans"
                  description="Create a custody schedule to track which household your children are with on each day. You can set up alternating weeks, split weeks, or custom patterns."
                  actionLabel="Create First Plan"
                  onAction={handleAddPlan}
                />
              ) : (
                <List disablePadding>
                  {custodyPlans.map((planWithId, index) => {
                    const { id, plan, households: planHouseholds } = planWithId;
                    const isActive = isPlanActive(plan.startDate, plan.endDate);
                    const isPast = isPlanPast(plan.endDate);
                    const startStr = formatCustodyDate(new Date(plan.startDate));
                    const endStr = formatCustodyDate(new Date(plan.endDate));

                    return (
                      <React.Fragment key={id}>
                        {index > 0 && <Divider />}
                        <ListItemButton
                          onClick={() => handleEditPlan(planWithId)}
                          sx={{
                            borderRadius: 1,
                            my: 0.5,
                            opacity: isPast ? 0.6 : 1,
                            border: isActive ? 2 : 0,
                            borderColor: 'primary.main',
                          }}
                        >
                          <ListItemIcon>
                            <Avatar
                              sx={{
                                bgcolor: isActive
                                  ? 'primary.main'
                                  : isPast
                                  ? 'grey.400'
                                  : 'grey.200',
                              }}
                            >
                              <CalendarIcon />
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                  {plan.custodySchemeText || 'Custody Plan'}
                                </Typography>
                                {isActive && (
                                  <Chip
                                    label="Active"
                                    size="small"
                                    color="success"
                                    sx={{ height: 20 }}
                                  />
                                )}
                                {isPast && (
                                  <Chip
                                    label="Ended"
                                    size="small"
                                    sx={{ height: 20, bgcolor: 'grey.200' }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 0.5 }}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  component="span"
                                >
                                  {startStr} - {endStr}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                    mt: 1,
                                  }}
                                >
                                  {Object.values(planHouseholds).map((hh) => (
                                    <Box
                                      key={hh.id}
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 10,
                                          height: 10,
                                          borderRadius: 0.5,
                                          bgcolor: hh.color,
                                        }}
                                      />
                                      <Typography variant="caption" color="text.secondary">
                                        {hh.name}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            }
                          />
                          <ChevronRightIcon color="action" />
                        </ListItemButton>
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <CustodyFormDialog
        open={formDialogOpen}
        onClose={() => {
          setFormDialogOpen(false);
          setSelectedPlan(null);
        }}
        onSave={handleSavePlan}
        existingPlan={selectedPlan}
        households={households}
        isNewPlan={isNewPlan}
        activePlanId={activePlan?.id}
      />

      <CustodySwapRequestDialog
        open={swapDialogOpen}
        onClose={() => setSwapDialogOpen(false)}
        custodyDates={custodyDates}
        households={households}
        mode={swapDialogMode}
        swapRequests={swapRequests}
      />
    </Box>
  );
};

export default CustodyPage;
